<script setup lang="ts">
import { ImgHTMLAttributes, toRef } from 'vue'
import type { Product } from '~/types/shopware'
import { getProductTranslatedCustomFields } from '~/lib/utils/shopware'
import { useSeoUrls } from '~/composables/useSeoUrls'

const props = defineProps<{
  product: Product
}>()

const description = computed(() => {
  const customFields = getProductTranslatedCustomFields(props.product)
  return customFields?.custom_product_fields_subtitle || ''
})

const { useProductSeoPath } = useSeoUrls()
const path = useProductSeoPath(toRef(props, 'product'))
const image = computed(() => {
  return {
    src: props.product?.cover?.media?.url || '',
    width: props.product?.cover?.media?.metaData?.width || 140,
    height: props.product?.cover?.media?.metaData?.height || 200,
    alt: props.product?.cover?.media?.alt || 'product thumbnail',
    loading: 'lazy',
  } as ImgHTMLAttributes
})
</script>
<template>
  <NuxtLink class="product" :to="path">
    <img v-if="image.src" class="image" v-bind="image" />
    <img
      v-else
      src="data:image/svg+xml;base64,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"
      alt="Placeholder"
      width="140"
      height="200"
      class="placeholder"
    />

    <div class="text">
      <h5 class="title">
        {{ product.translated.name }}
      </h5>
      <p v-if="description" class="description text-xsmall">
        {{ description }}
      </p>
    </div>

    <Icon class="icon" name="link-arrow" />
  </NuxtLink>
</template>
<style scoped lang="scss">
.product {
  display: grid;
  grid-template-columns: 60px 1fr;
  align-items: center;
  gap: 24px;
  padding: 24px;
  background-color: var(--c-background-medium);
  @include breakpoint(ts) {
    grid-template-columns: 80px 1fr 24px;
    gap: 40px;
  }
}
.image {
  object-fit: cover;
  aspect-ratio: 400/563;
}
.title {
  margin-bottom: 3px;
}
.description {
  margin-bottom: 0;
}

.icon {
  @include breakpoint(ts down) {
    display: none;
  }
}
</style>
